/**
 * Rules for dashboard "deal stage movement" (positive forward vs lost).
 * Stage labels come from history_events.detail.fromStage / toStage (stages.name at write time).
 */

/** Team Volanie tel_click count threshold for "high call volume" day (inclusive). */
export const DEAL_STAGE_MOVEMENT_HIGH_CALLS_MIN = 5;

/** Normalize for comparison: lowercase, trim, collapse spaces, strip combining marks. */
export function normalizeStageLabel(raw: string): string {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!s) return "";
  try {
    return s.normalize("NFD").replace(/\p{M}/gu, "");
  } catch {
    return s;
  }
}

/** Match lost destination (aligned with isLostStageRow on plain name). */
export function isLostStageName(name: string): boolean {
  const n = normalizeStageLabel(name);
  if (!n) return false;
  if (n === "closed lost" || n === "deal lost") return true;
  return n.includes("lost");
}

function includesAll(hay: string, parts: string[]): boolean {
  return parts.every((p) => hay.includes(p));
}

/** Early / source stages for a "positive forward" move. */
export function isPositiveMovementFromStage(name: string): boolean {
  const n = normalizeStageLabel(name);
  if (!n) return false;

  if (
    n === "uj megkereses" ||
    n.includes("uj megkereses") ||
    n.includes("novy kontakt") ||
    n.includes("new inquiry")
  )
    return true;
  if (n.includes("kell arajanlat") || n.includes("potrebujeme cenovu")) return true;
  if (n.includes("jovahagyasra varunk") || n.includes("cakame na schvalenie")) return true;
  if (n.includes("closed won")) return true;

  if (includesAll(n, ["majd", "jelentkezik"]) && (n.includes("hivogass") || n.includes("ne hivogassuk"))) return true;

  return false;
}

/** Target stages that count as positive forward (not lost). Closed Won / B opció / won-like labels are intentionally neutral. */
export function isPositiveMovementToStage(name: string): boolean {
  const n = normalizeStageLabel(name);
  if (!n) return false;
  if (isLostStageName(name)) return false;

  const checks: (() => boolean)[] = [
    () => n.includes("ki kell mennunk szemelyesen"),
    () => n.includes("osobne musime ist") || n.includes("personal visit"),
    () => (n.includes("zalo") || n.includes("zaloha")) && n.includes("befizetes"),
    () => n.includes("zaro") && n.includes("befizetes"),
    () => n.includes("vegso jovahagyasra varunk") || n.includes("finalne schvalenie"),
    () => n.includes("meg kell reldelni") || n.includes("meg kell rendelni"),
    () => n.includes("megrendelve") || n.includes("objednane"),
    () => n.includes("befizettuk") || n.includes("zaplatili sme"),
    () => n.includes("kapu nalunk") || n.includes("brana u nas"),
    () => n.includes("idopont") && (n.includes("leegyezve") || n.includes("egyeztetve") || n.includes("egyezve")),
    () => n.includes("partner megkereses") || n.includes("partner inquiry"),
  ];

  return checks.some((c) => c());
}

export type StageMovementKind = "positive" | "negative" | "neutral";

export function classifyStageMovement(fromStage: string, toStage: string): StageMovementKind {
  if (isLostStageName(toStage)) return "negative";
  if (isPositiveMovementFromStage(fromStage) && isPositiveMovementToStage(toStage)) return "positive";
  return "neutral";
}
